
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Anchor, Settings, Package, Clipboard, Bot } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import NauticLogo from '@/components/ui/NauticLogo';
import Footer from '@/components/layout/Footer';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Rediriger vers le dashboard si l'utilisateur est connecté
  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Afficher un loader pendant le chargement de l'auth
  if (user === undefined) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nautic mx-auto mb-4"></div>
          <p className="text-nautic">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header avec fond bleu et vague */}
      <div className="relative bg-nautic text-white pb-6 md:pb-7">
        <div className="container mx-auto flex justify-center">
          <img 
            src="/lovable-uploads/NAUTICOACH_2.png" 
            alt="NAUTICOACH.com" 
            className="w-full object-contain -mt-2 md:-mt-4"
          />
        </div>
        {/* Vague décorative */}
        <div className="absolute -bottom-px left-0 w-full overflow-hidden leading-none">
          <svg
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            className="w-full h-[30px] md:h-[80px]"
          >
            <path
              d="M0,40 C240,100 480,0 720,50 C960,100 1200,10 1440,60 L1440,120 L0,120 Z"
              className="fill-white"
            />
          </svg>
        </div>
      </div>

      {/* Section principale */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">



          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/auth">
              <Button className="bg-nautic-gold hover:bg-nautic-lightgold text-nautic px-8 py-3 text-lg font-semibold">
                Commencer maintenant
              </Button>
            </Link>
            <Link to="/auth">
              <Button variant="outline" className="border-nautic-gold text-nautic hover:bg-nautic-gold hover:text-white px-8 py-3 text-lg font-semibold">
                Se connecter
              </Button>
            </Link>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-nautic mb-4">
            Bienvenue à bord de NautiCoach, votre assistant nautique boosté à l'IA.
          </h1>
          
          <p className="text-xl md:text-2xl font-semibold text-nautic-gold mb-3">
            Gérez, entretenez et naviguez l'esprit tranquille.
          </p>
          
          <p className="text-base md:text-lg text-muted-foreground mb-4 max-w-2xl mx-auto">
            Votre bateau mérite le meilleur : inventaire, maintenance, checklists, avitaillement, documents… tout au même endroit, simplement et sans prise de tête.
            <br />
            Grâce à l'IA, vous avez un vrai expert disponible 24/7, prêt à vous conseiller à tout moment.
          </p>

          <p className="text-lg md:text-xl font-semibold text-nautic mb-4">
            Prêt à le tester gratuitement ?
          </p>

          {/* Prix de lancement */}
          <div className="relative overflow-hidden bg-gradient-to-r from-nautic/5 via-nautic-gold/10 to-nautic/5 border border-nautic-gold/30 rounded-2xl p-5 max-w-xl mx-auto mb-6 animate-[shimmer_3s_ease-in-out_infinite]">
            <div className="absolute inset-0 -translate-x-full animate-[shine_3s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-nautic-gold/20 to-transparent" />
            <p className="text-sm font-semibold text-nautic uppercase tracking-wide mb-2">🎉 Offre de lancement • Valable jusqu'au 31/12/2026</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
              <div className="text-center">
                <span className="text-2xl md:text-3xl font-bold text-nautic">4,99 €</span>
                <span className="text-muted-foreground text-sm">/mois</span>
              </div>
              <span className="hidden sm:block text-muted-foreground text-lg">ou</span>
              <span className="sm:hidden text-muted-foreground text-sm">ou</span>
              <div className="text-center">
                <span className="text-2xl md:text-3xl font-bold text-nautic">54,99 €</span>
                <span className="text-muted-foreground text-sm">/an</span>
                <span className="block text-xs font-medium text-nautic-gold">dont 1 mois offert</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 justify-center mb-8">
            <span className="inline-flex items-center gap-1.5 bg-nautic/10 text-nautic px-4 py-2 rounded-full text-sm font-medium">
              ✅ 100% Gratuit
            </span>
            <span className="inline-flex items-center gap-1.5 bg-nautic-gold/15 text-nautic px-4 py-2 rounded-full text-sm font-medium">
              ⭐ Abonnement Premium
            </span>
            <span className="inline-flex items-center gap-1.5 bg-nautic/10 text-nautic px-4 py-2 rounded-full text-sm font-medium">
              ⚓ Conçu par des passionnés
            </span>
            <span className="inline-flex items-center gap-1.5 bg-nautic/10 text-nautic px-4 py-2 rounded-full text-sm font-medium">
              🚀 Simple & Efficace
            </span>
          </div>

          {/* Card décorative avec jauges */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
            {/* Card maintenance */}
            <Card className="border-nautic-gold/30 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <p className="text-sm font-semibold text-nautic mb-3">🔧 Suivi de maintenance</p>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-xs text-nautic">Vidange moteur</span>
                    </div>
                    <span className="text-xs font-medium text-green-600">✅ Terminée</span>
                  </div>
                  <div className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                      <span className="text-xs text-nautic">Antifouling coque</span>
                    </div>
                    <span className="text-xs font-medium text-amber-600">⏳ Prévue</span>
                  </div>
                  <div className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-xs text-nautic">Révision winch</span>
                    </div>
                    <span className="text-xs font-medium text-red-600">🔴 En retard</span>
                  </div>
                </div>
                <div className="mt-3 bg-nautic/5 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Coût maintenance 2026</p>
                  <p className="text-2xl font-bold text-nautic-gold">1 250 €</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Budget maîtrisé grâce au suivi</p>
                </div>
              </CardContent>
            </Card>

            {/* Card conversation assistant */}
            <Card className="border-nautic-gold/30 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <p className="text-sm font-semibold text-nautic mb-3">🤖 Assistant IA</p>
                <div className="space-y-2.5">
                  <div className="flex justify-end">
                    <div className="bg-nautic text-white text-xs px-3 py-2 rounded-2xl rounded-br-sm max-w-[85%]">
                      Quel temps fait-il demain pour naviguer ?
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-nautic-gold/15 text-nautic text-xs px-3 py-2 rounded-2xl rounded-bl-sm max-w-[85%]">
                      ⛵ Demain à Le Brusc : vent NW 12 nœuds, mer belle, ensoleillement 80%. Conditions idéales pour naviguer ! 🌤️
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-nautic text-white text-xs px-3 py-2 rounded-2xl rounded-br-sm max-w-[85%]">
                      Ma vidange moteur est à jour ?
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-nautic-gold/15 text-nautic text-xs px-3 py-2 rounded-2xl rounded-bl-sm max-w-[85%]">
                      ⚠️ Dernière vidange il y a 8 mois. Je recommande de planifier ça avant votre prochaine sortie ! 🔧
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Deuxième rangée de cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
            {/* Card inventaire */}
            <Card className="border-nautic-gold/30 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <p className="text-sm font-semibold text-nautic mb-3">📦 Inventaire & Avitaillement IA</p>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between rounded-lg px-3 py-2 bg-nautic/5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs">📡</span>
                      <span className="text-xs text-nautic font-medium">Radio VHF</span>
                    </div>
                    <span className="text-xs font-medium text-green-600">1/1 ✅</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg px-3 py-2 bg-amber-50">
                    <div className="flex items-center gap-2">
                      <span className="text-xs">🥕</span>
                      <span className="text-xs text-nautic font-medium">Carottes</span>
                    </div>
                    <span className="text-xs font-medium text-amber-600">2/4 ⚠️</span>
                  </div>
                </div>

                {/* Section avitaillement IA */}
                <div className="mt-3 border-t border-nautic-gold/20 pt-3">
                  <p className="text-xs font-semibold text-nautic mb-2">🤖 Avitaillement généré par l'IA</p>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center gap-1 bg-nautic/10 rounded-full px-2.5 py-1">
                      <span className="text-[10px]">👥</span>
                      <span className="text-[10px] font-medium text-nautic">4 personnes</span>
                    </div>
                    <div className="flex items-center gap-1 bg-nautic/10 rounded-full px-2.5 py-1">
                      <span className="text-[10px]">📅</span>
                      <span className="text-[10px] font-medium text-nautic">5 jours</span>
                    </div>
                  </div>
                  <div className="bg-nautic-gold/10 rounded-lg p-2.5 space-y-1.5">
                    <div className="flex justify-between text-[11px]">
                      <span className="text-nautic">💧 Eau (1.5L)</span>
                      <span className="font-medium text-nautic">14 bouteilles</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-nautic">🥖 Pain</span>
                      <span className="font-medium text-nautic">5 unités</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-nautic">🍝 Pâtes (500g)</span>
                      <span className="font-medium text-nautic">5 paquets</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-nautic">🥩 Viande / Protéines</span>
                      <span className="font-medium text-nautic">3 kg</span>
                    </div>
                  </div>
                  <p className="text-[9px] text-muted-foreground text-center mt-1.5">L'IA calcule les quantités selon l'équipage et la durée</p>
                </div>
              </CardContent>
            </Card>

            {/* Card checklist */}
            <Card className="border-nautic-gold/30 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <p className="text-sm font-semibold text-nautic mb-3">✅ Checklist — Arrivée au port</p>
                <div className="space-y-2">
                  {[
                    { tache: 'Sécuriser les amarres', done: true },
                    { tache: 'Éteindre le moteur', done: true },
                    { tache: 'Brancher l\'électricité', done: true },
                    { tache: 'Nettoyer le pont et l\'ancre', done: false },
                    { tache: 'Contrôler eau et carburant', done: false },
                    { tache: 'Fermeture vannes', done: false },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${item.done ? 'bg-green-50' : 'bg-nautic/5'}`}>
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${item.done ? 'border-green-500 bg-green-500' : 'border-muted-foreground/30'}`}>
                        {item.done && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className={`text-xs ${item.done ? 'line-through text-muted-foreground' : 'text-nautic'}`}>{item.tache}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2">
                  <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                    <span>Progression</span>
                    <span className="font-medium text-nautic">3/6</span>
                  </div>
                  <Progress value={50} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </div>
          


        </div>

        {/* Fonctionnalités principales */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-nautic text-center mb-12">
            Fonctionnalités Principales
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 max-w-7xl mx-auto">
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border">
              <div className="bg-nautic text-nautic-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Anchor className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-nautic mb-2">Gestion des Bateaux</h3>
              <p className="text-gray-600 text-sm">
                Enregistrez et gérez tous les détails de vos bateaux en un seul endroit
              </p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border">
              <div className="bg-nautic text-nautic-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Settings className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-nautic mb-2">Suivi de Maintenance</h3>
              <p className="text-gray-600 text-sm">
                Planifiez et suivez toutes les opérations de maintenance de votre flotte.
              </p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border">
              <div className="bg-nautic text-nautic-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-nautic mb-2">Inventaire & Avitaillement</h3>
              <p className="text-gray-600 text-sm">
                Gérez votre inventaire d'équipements et votre avitaillement avec facilité.
              </p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border">
              <div className="bg-nautic text-nautic-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clipboard className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-nautic mb-2">Checklists</h3>
              <p className="text-gray-600 text-sm">
                Créez des listes de vérification pour toutes vos procédures nautiques.
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-sm border">
              <div className="bg-nautic text-nautic-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bot className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-nautic mb-2">Assistant IA</h3>
              <p className="text-gray-600 text-sm">
                Votre coach personnel avec intelligence artificielle pour tous vos besoins nautiques.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HomePage;
